/**
 * winterStream — shared streaming client for Ask Winter.
 *
 * Talks to POST /api/ask-winter/stream (SSE over fetch) and falls back
 * to the plain JSON endpoint when streaming isn't available — a 404
 * during deploy skew, a proxy that buffers, an old backend. Callers
 * get the same resolved shape either way.
 *
 * streamWinterReply({ apiBase, authToken, body, signal, onDelta, onTool })
 *   → Promise<{ reply, contextLoaded, streamed }>
 *
 *   onDelta(textChunk)      — incremental reply text (streamed mode only)
 *   onTool({ name, label }) — Winter started a tool call ("Searching
 *                             call transcripts…") — drive an activity chip
 *
 * The resolved `reply` is ALWAYS the full text: in streamed mode it's
 * the accumulated deltas (which include Winter's natural preambles
 * before tool calls), falling back to the server's authoritative
 * `done.reply` if no deltas arrived.
 *
 * Abort: pass an AbortSignal — aborting rejects with err.name ===
 * 'AbortError'; callers decide whether to keep partial text.
 */

export async function streamWinterReply({ apiBase, authToken, body, signal, onDelta, onTool }) {
  // ── Try the streaming endpoint first ──────────────────────────────
  let resp = null;
  try {
    resp = await fetch(`${apiBase || ''}/api/ask-winter/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: authToken ? `Bearer ${authToken}` : '',
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err && err.name === 'AbortError') throw err;
    resp = null; // network-level failure — try the JSON endpoint
  }

  const contentType = resp ? (resp.headers.get('content-type') || '') : '';

  if (resp && resp.ok && contentType.includes('text/event-stream') && resp.body) {
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulated = '';
    let doneEvent = null;

    const processFrame = (frame) => {
      // A frame may hold several `data:` lines (spec allows it); ours
      // emits one JSON object per frame.
      const dataLines = frame.split('\n').filter((l) => l.startsWith('data:'));
      if (!dataLines.length) return;
      let evt;
      try {
        evt = JSON.parse(dataLines.map((l) => l.slice(5).trim()).join(''));
      } catch (_) {
        return; // malformed frame — skip
      }
      if (evt.type === 'delta' && typeof evt.text === 'string') {
        accumulated += evt.text;
        if (onDelta) onDelta(evt.text);
      } else if (evt.type === 'tool') {
        if (onTool) onTool({ name: evt.name, label: evt.label || evt.name });
      } else if (evt.type === 'done') {
        doneEvent = evt;
      } else if (evt.type === 'error') {
        throw new Error(evt.message || 'Winter had trouble answering that.');
      }
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        processFrame(frame);
      }
    }
    if (buffer.trim()) processFrame(buffer); // trailing frame without final \n\n

    const reply = accumulated || (doneEvent && doneEvent.reply) || '';
    if (!reply && !doneEvent) {
      throw new Error('Winter had trouble answering that. Try again in a moment.');
    }
    return {
      reply,
      contextLoaded: doneEvent ? doneEvent.contextLoaded !== false : true,
      streamed: true,
    };
  }

  // ── Fallback: plain JSON endpoint ──────────────────────────────────
  const jsonResp = await fetch(`${apiBase || ''}/api/ask-winter`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authToken ? `Bearer ${authToken}` : '',
    },
    body: JSON.stringify(body),
    signal,
  });
  const data = await jsonResp.json().catch(() => ({}));
  if (!jsonResp.ok) {
    throw new Error(data.reply || data.error || "Winter couldn't answer that right now.");
  }
  return {
    reply: data.reply || '',
    contextLoaded: data.contextLoaded !== false,
    streamed: false,
  };
}
